import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import styled from 'styled-components';

import Text from '@/components/Text';
import Tooltiped from '@/components/Tooltiped';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';

const Button = styled(Text)`
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  background: ${({ theme }) => theme.colors.white};
  transition: 0.2s ease-out all;

  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
    color: ${({ theme }) => theme.colors.black};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

interface Props {
  channelId: number;
  hasFailedPosts: boolean;
}

const ClearFailedPostsButton: FC<Props> = ({ channelId, hasFailedPosts }) => {
  const [network] = useNetwork();
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const onClick = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      await api.post(`/v1/editorial/queue/clear-failed-posts`, {
        channel_id: channelId,
        network,
      });

      await queryClient.invalidateQueries(['editorial_distribution_schedules']);

      queryClient.invalidateQueries(['editorial_distribution_queue_channels']);

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <Tooltiped title="Clear All">
      <Button
        onClick={onClick}
        $color="gray"
        as="button"
        type="button"
        $type="smallHeadline"
        disabled={!hasFailedPosts}
      >
        <svg
          width="15"
          height="16"
          viewBox="0 0 15 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.6875 3.23828L1.3125 3.23829"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.8125 6.66602V11.2374"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.1875 6.66602V11.2374"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12.5625 3.23828V14.0954C12.5625 14.247 12.5032 14.3923 12.3977 14.4995C12.2923 14.6066 12.1492 14.6669 12 14.6669H3C2.85082 14.6669 2.70774 14.6066 2.60225 14.4995C2.49676 14.3923 2.4375 14.247 2.4375 14.0954V3.23828"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.3125 3.23884V2.09598C10.3125 1.79288 10.194 1.50219 9.98299 1.28786C9.77202 1.07353 9.48587 0.953125 9.1875 0.953125H5.8125C5.51413 0.953125 5.22798 1.07353 5.01701 1.28786C4.80603 1.50219 4.6875 1.79288 4.6875 2.09598V3.23884"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Button>
    </Tooltiped>
  );
};

export default ClearFailedPostsButton;
